import { useState, useMemo, useEffect } from "react";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";
import { createPortal } from "react-dom";
import PaginationComponent from "../../../components/Atomos/Pagination/PaginationComponent";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Excel from "../../../components/Atomos/Icons/Excel";
import {
  exportStyledExcel,
  getExcelTitleFromFilename,
} from "../../../utils/excel/exportStyledExcel";
import { MAX_TABLE_PAGE_SIZE } from "../../../constants/tablePagination";

export interface DataTableModalColumn {
  header: string;
  accessorKey: string;
  format?: (value: unknown, row: Record<string, unknown>) => React.ReactNode;
}

export interface DataTableModalTab {
  label: string;
  columns: DataTableModalColumn[];
  data: Record<string, unknown>[];
  showExportToExcel?: boolean;
}

export interface DataTableModalConfig {
  title: string;
  columns?: DataTableModalColumn[];
  data?: Record<string, unknown>[];
  tabs?: DataTableModalTab[];
  inactiveKey?: string;
  showExportToExcel?: boolean;
  initialTabIndex?: number;
}

interface DataTableModalProps {
  config: DataTableModalConfig | null;
  onClose: () => void;
}

const DEFAULT_PAGE_SIZE = MAX_TABLE_PAGE_SIZE;

export default function DataTableModal({ config, onClose }: DataTableModalProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const columnHelper = createColumnHelper<Record<string, unknown>>();

  useEffect(() => {
    setActiveTabIndex(config?.initialTabIndex ?? 0);
    setSorting([]);
    setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, [config]);

  const effectiveTab = config?.tabs?.[activeTabIndex];
  const effectiveColumns = effectiveTab?.columns ?? config?.columns ?? [];
  const effectiveData = effectiveTab?.data ?? config?.data ?? [];
  const effectiveShowExport =
    effectiveTab?.showExportToExcel ?? config?.showExportToExcel ?? false;

  const handleExportToExcel = async () => {
    if (!effectiveData.length || !effectiveColumns.length) {
      return;
    }
    const rows = effectiveData.map((row) => {
      const out: Record<string, string | number> = {};
      effectiveColumns.forEach((col) => {
        const val = row[col.accessorKey];
        const display =
          col.format != null
            ? col.format(val, row)
            : val !== null && val !== undefined
              ? String(val)
              : "";
        out[col.header] = typeof display === "string" || typeof display === "number" ? display : String(val ?? "");
      });
      return out;
    });
    const filename = `${config!.title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    await exportStyledExcel({
      filename,
      title: getExcelTitleFromFilename(filename),
      rows,
      columns: effectiveColumns.map((col) => ({
        key: col.header,
        header: col.header,
      })),
    });
  };

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    if (!config) {
      return [];
    }

    const numberColumn = columnHelper.display({
      id: "rowNumber",
      header: "#",
      cell: (info) => {
        const paginatedRows = info.table.getRowModel().rows;
        const rowIndex = paginatedRows.findIndex((row) => row.id === info.row.id);
        const { pageIndex, pageSize } = info.table.getState().pagination;
        return pageIndex * pageSize + rowIndex + 1;
      },
    });

    const dataColumns: ColumnDef<Record<string, unknown>>[] = effectiveColumns.map((col) =>
      columnHelper.accessor((row) => row[col.accessorKey], {
        id: col.accessorKey,
        header: ({ column }) => (
          <div
            className="flex gap-1 cursor-pointer select-none p-1 rounded"
            onClick={() => {
              const current = column.getIsSorted();
              if (current === false) {
                column.toggleSorting(false);
              }
              else if (current === "asc") {
                column.toggleSorting(true);
              }
              else {
                column.clearSorting();
              }
            }}
          >
            <span>{col.header}</span>
            <span className="text-white">
              {column.getIsSorted() === "asc" ? "↑" : column.getIsSorted() === "desc" ? "↓" : "⇅"}
            </span>
          </div>
        ),
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          const value = info.getValue();
          const isInactive = config.inactiveKey != null && (row[config.inactiveKey] as boolean) === false;
          if (
            config.tabs &&
            config.tabs.length > 1 &&
            activeTabIndex === 0 &&
            col.accessorKey === "guests" &&
            typeof value === "number" &&
            value > 0
          ) {
            return (
              <button
                type="button"
                className="text-[#0171CC] underline font-semibold"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTabIndex(1);
                  setSorting([]);
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
              >
                {value}
              </button>
            );
          }
          const content = col.format != null ? col.format(value, row) : (value != null ? String(value) : "");
          return (
            <span
              className={isInactive ? "text-gray-400 font-normal" : "text-gray-900 font-semibold"}
            >
              {content}
            </span>
          );
        },
      })
    );

    return [numberColumn, ...dataColumns];
  }, [config, columnHelper, effectiveColumns, activeTabIndex]);

  const table = useReactTable({
    data: effectiveData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: false,
  });

  const handlePageChange = (pageNumber: number, pageSize: number) => {
    const newPageIndex = Math.max(0, pageNumber - 1);
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex, pageSize }));
    table.setPageIndex(newPageIndex);
    if (pageSize !== pagination.pageSize) {
      table.setPageSize(pageSize);
    }
  };

  if (!config) {
    return null;
  }

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      <div
        className="bg-white shadow-xl max-w-[110rem] max-h-[90vh] overflow-hidden flex flex-col rounded-lg"
        style={{ zIndex: 100000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3 p-6 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-gray-800">{config.title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          {config.tabs && config.tabs.length > 0 && (
            <div className="flex flex-wrap gap-1" role="tablist">
              {config.tabs.map((tab, i) => {
                const isActive = i === activeTabIndex;
                return (
                  <button
                    key={tab.label}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => {
                      setActiveTabIndex(i);
                      setSorting([]);
                      setPagination((p) => ({ ...p, pageIndex: 0 }));
                    }}
                    className={
                      isActive
                        ? "rounded px-4 py-2 text-sm font-semibold bg-[#E4F3FF] text-[#0171CC]"
                        : "rounded px-4 py-2 text-sm font-semibold bg-accent-Default-dark text-white"
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="overflow-auto flex-1 px-6 py-4">
          <div className="w-full overflow-x-auto">
            <table className="min-w-full w-max border-collapse">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-accent-Default-dark">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className=" text-white font-semibold p-2 whitespace-nowrap"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="even:bg-gray-100">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="p-2 text-left">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200">
          <PaginationComponent
            recordsPerPage={pagination.pageSize}
            currentPage={pagination.pageIndex + 1}
            onPageChange={handlePageChange}
            totalCount={effectiveData.length}
          />
        </div>
        <div className="pb-6 px-6 flex justify-end gap-2">
          {effectiveShowExport && (
            <LegacyButton
              text="Export to Excel"
              color="excel"
              content="textIcon"
              onClick={handleExportToExcel}
              icon={
                <div className="w-5 h-5 ml-2 flex items-center justify-center">
                  <Excel />
                </div>
              }
              className="w-full lg:w-fit"
            />
          )}
          <LegacyButton
            text="DONE"
            color="accent"
            content="textIcon"
            onClick={onClose}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            }
            className="w-full lg:w-fit"
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
